import { useEffect, useRef, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  doc,
} from "firebase/firestore";

/* =====================================================
   DATE HELPERS
===================================================== */
const toISODate = (value) => {
  if (!value) return "";
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "string") return value.slice(0, 10);
  return "";
};

const displayDate = (value) => {
  if (!value) return "—";
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

/* =====================================================
   MODAL
===================================================== */
function RaceModal({ open, onClose, onSubmit, initialRace }) {
  const dateRef = useRef(null);

  const [form, setForm] = useState({
    round: "",
    name: "",
    circuit: "",
    dateISO: "",
    status: "scheduled",
  });

  /* INIT */
  useEffect(() => {
    if (!open) return;

    if (initialRace) {
      setForm({
        round: String(initialRace.round),
        name: initialRace.name,
        circuit: initialRace.circuit || "",
        dateISO: toISODate(initialRace.startDate),
        status: initialRace.status,
      });
    } else {
      setForm({
        round: "",
        name: "",
        circuit: "",
        dateISO: "",
        status: "scheduled",
      });
    }

    setTimeout(() => {
      dateRef.current?.focus();
      dateRef.current?.showPicker?.();
    }, 100);
  }, [open, initialRace]);

  if (!open) return null;

  /* VALIDATION */
  const validate = () => {
    const round = Number(form.round);
    if (!round || round <= 0) return "Round is required";
    if (!form.name.trim()) return "Grand Prix name is required";
    if (!form.dateISO) return "Race date is required";

    const date = new Date(form.dateISO);
    if (isNaN(date)) return "Invalid date";

    if (form.status === "completed" && date > new Date()) {
      return "Completed races cannot be in the future";
    }

    return null;
  };

  const handleSave = () => {
    const error = validate();
    if (error) {
      alert(error);
      return;
    }

    const date = new Date(form.dateISO);
    const season = String(date.getFullYear()); // 🔥 ALWAYS DERIVED HERE

    onSubmit({
      season,
      round: Number(form.round),
      name: form.name.trim(),
      circuit: form.circuit.trim(),
      status: form.status,
      startDate: date,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-neutral-900 w-full max-w-md rounded-xl border border-neutral-800 p-6">
        <h3 className="text-lg font-semibold mb-4">
          {initialRace ? "Edit Race" : "Add Race"}
        </h3>

        <div className="space-y-3">
          <input
            type="number"
            placeholder="Round"
            value={form.round}
            onChange={(e) =>
              setForm({ ...form, round: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            placeholder="Grand Prix Name"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            placeholder="Circuit Name"
            value={form.circuit}
            onChange={(e) =>
              setForm({ ...form, circuit: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            ref={dateRef}
            type="date"
            value={form.dateISO}
            onChange={(e) =>
              setForm({ ...form, dateISO: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          >
            <option value="scheduled">Scheduled</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="text-neutral-400">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded-lg text-white font-semibold"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   MAIN
===================================================== */
export default function RaceCalendar({ admin }) {
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");
  const [races, setRaces] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRace, setEditingRace] = useState(null);

  /* LOAD SEASONS */
  useEffect(() => {
    return onSnapshot(collection(db, "calendar"), (snap) => {
      const years = [
        ...new Set(snap.docs.map((d) => d.data().season)),
      ].sort((a, b) => b - a);

      setSeasons(years);

      // Bootstrap: auto-select latest season if exists
      if (!activeSeason && years.length) {
        setActiveSeason(years[0]);
      }
    });
  }, [activeSeason]);

  /* LOAD RACES */
  useEffect(() => {
    if (!activeSeason) {
      setRaces([]);
      return;
    }

    return onSnapshot(
      query(
        collection(db, "calendar"),
        where("season", "==", activeSeason)
      ),
      (snap) => {
        setRaces(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => a.round - b.round)
        );
      }
    );
  }, [activeSeason]);

  /* SAVE */
  const handleSave = async (data) => {
    if (editingRace) {
      await updateDoc(doc(db, "calendar", editingRace.id), {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } else {
      await addDoc(collection(db, "calendar"), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: admin.email,
      });
    }

    setModalOpen(false);
    setEditingRace(null);
  };

  return (
    <div className="bg-neutral-900/80 p-6 rounded-2xl border border-neutral-800">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold">Race Calendar</h2>
          <p className="text-sm text-neutral-400">
            Manage the scheduled races for the season.
          </p>
        </div>

        <div className="flex gap-3 items-center">
          <select
            value={activeSeason}
            onChange={(e) => setActiveSeason(e.target.value)}
            className="h-10 w-32 bg-neutral-950 border border-neutral-800 px-4 rounded-lg"
          >
            <option value="">All</option>
            {seasons.map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>
          
          <button
            onClick={() => setModalOpen(true)}
            className="h-10 w-32 px-2 bg-red-600 hover:bg-red-500 rounded-lg text-white font-semibold"
          >
            + Add Race
          </button>
        </div>
      </div>

      {/* TABLE */}
      {races.length === 0 ? (
        <div className="text-sm text-neutral-500 py-8 text-center">
          {seasons.length === 0
            ? "No races yet. Add your first race to create a season."
            : "No races for this season."}
        </div>
      ) : (
        <div className="border border-neutral-800 rounded-xl overflow-hidden">
          {races.map((r) => (
            <div
              key={r.id}
              className="grid grid-cols-12 px-4 py-4 border-b border-neutral-800 hover:bg-neutral-900"
            >
              <div className="col-span-2">
                {displayDate(r.startDate)}
              </div>
              <div className="col-span-4">
                {r.name}
                <div className="text-xs text-neutral-500">
                  Round {r.round}
                </div>
              </div>
              <div className="col-span-3 text-neutral-400">
                {r.circuit || "—"}
              </div>
              <div className="col-span-2">{r.status}</div>
              <div className="col-span-1 text-right space-x-3">
                <button
                  onClick={() => {
                    setEditingRace(r);
                    setModalOpen(true);
                  }}
                  className="text-neutral-400"
                >
                  Edit
                </button>
                <button
                  onClick={() =>
                    deleteDoc(doc(db, "calendar", r.id))
                  }
                  className="text-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RaceModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingRace(null);
        }}
        onSubmit={handleSave}
        initialRace={editingRace}
      />
    </div>
  );
}
